import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Field, PageHeader, LoadingSpinner } from './v2-utils';
import '../styles/v2.css';

export default function ReconcileInventoryV2() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inventory, setInventory]           = useState(null);
  const [units, setUnits]                   = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [currentStockDisplay, setCurrentStockDisplay] = useState('');
  const [actualStock, setActualStock]       = useState('');
  const [stockLimit, setStockLimit]         = useState('');
  const [targetStock, setTargetStock]       = useState('');
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invRes, reorderRes] = await Promise.all([
          axiosInstance.get(`/inventories/${id}`),
          axiosInstance.get('/inventories/reorder-candidates').catch(() => ({ data: { reorderCandidates: [] } })),
        ]);
        const inv = invRes.data;
        setInventory(inv);
        setStockLimit(inv.stock_limit != null ? String(inv.stock_limit) : '');
        setTargetStock(inv.target_stock != null ? String(inv.target_stock) : '');

        let unitList = [];
        const candidates = reorderRes.data?.reorderCandidates || [];
        const candidate = candidates.find(c => String(c.inventoryId) === String(id) || String(c.productId) === String(inv.product_id));

        if (candidate?.unitTypes?.length) {
          unitList = candidate.unitTypes.map(u => ({
            unit_id: u.unitId,
            unit_type: u.unitType,
            unit_category: u.unitCategory || '',
            current_stock: u.currentStock ?? inv.current_stock,
          }));
        }

        if (!unitList.length && inv.product_id) {
          try {
            const basicRes = await axiosInstance.get(`/units/product/${inv.product_id}/unitInfo`);
            const basicArr = Array.isArray(basicRes.data) ? basicRes.data : basicRes.data.units || [];
            unitList = basicArr.map(u => ({ unit_id: u.unit_id, unit_type: u.unit_type, unit_category: u.unit_category || '', current_stock: inv.current_stock }));
          } catch {}
        }

        if (inv.unit_id && !unitList.find(u => String(u.unit_id) === String(inv.unit_id))) {
          unitList.push({ unit_id: inv.unit_id, unit_type: inv.unit_type || 'Current Unit', unit_category: '', current_stock: inv.current_stock });
        }

        setUnits(unitList);
        const defUnit = unitList.find(u => String(u.unit_id) === String(inv.unit_id)) || unitList[0];
        if (defUnit) {
          setSelectedUnitId(String(defUnit.unit_id));
          setCurrentStockDisplay(defUnit.current_stock != null ? Number(defUnit.current_stock).toFixed(2) : '—');
        }
      } catch { setToast({ message: 'Failed to load inventory data.', severity: 'error' }); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  const handleUnitChange = (unitId) => {
    setSelectedUnitId(unitId);
    const unit = units.find(u => String(u.unit_id) === String(unitId));
    setCurrentStockDisplay(unit?.current_stock != null ? Number(unit.current_stock).toFixed(2) : '—');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(actualStock);
    if (actualStock === '' || isNaN(val)) { setToast({ message: 'Please enter a valid stock number.', severity: 'error' }); return; }
    if (val < 0) { setToast({ message: 'Stock cannot be negative.', severity: 'error' }); return; }
    setSaving(true);
    try {
      await Promise.all([
        axiosInstance.post(`/inventories/reconcile/${id}`, { actual_stock: val, actual_unit_id: selectedUnitId }),
        axiosInstance.put(`/inventories/${id}/limit`, {
          stock_limit: stockLimit === '' ? 0 : parseFloat(stockLimit),
          unit_id: selectedUnitId,
        }),
        axiosInstance.put(`/inventories/${id}/target-stock`, {
          target_stock: targetStock === '' ? null : parseFloat(targetStock),
        }),
      ]);
      setToast({ message: 'Inventory updated successfully!', severity: 'success' });
      setTimeout(() => navigate('/new/dashboard/inventories/view'), 1200);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update inventory.', severity: 'error' });
    } finally { setSaving(false); }
  };

  const productDisplay = inventory
    ? [inventory.product_name, inventory.brand && `(${inventory.brand})`, inventory.variety && `- ${inventory.variety}`, inventory.size && `[${inventory.size}]`].filter(Boolean).join(' ')
    : '';

  if (loading) return <LoadingSpinner message="Loading inventory…" />;

  return (
    <div style={{ maxWidth: 620, margin: '0 auto' }}>
      <PageHeader title="Reconcile Inventory" subtitle="Update stock count and thresholds for this item" onBack={() => navigate('/new/dashboard/inventories/view')} />

      <form onSubmit={handleSubmit}>
        <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Product (readonly) */}
          <Field label="Product">
            <div style={{ padding: '11px 14px', borderRadius: 10, background: '#f8fafc', border: '1.5px solid #e2e8f0', fontSize: 14, fontWeight: 600, color: '#334155' }}>
              {productDisplay || '—'}
            </div>
          </Field>

          {/* Unit select */}
          <Field label="Unit *">
            <div style={{ position: 'relative' }}>
              <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                value={selectedUnitId} onChange={e => handleUnitChange(e.target.value)} required>
                {units.map(u => (
                  <option key={u.unit_id} value={String(u.unit_id)}>
                    {u.unit_type}{u.unit_category ? ` (${u.unit_category})` : ''}
                  </option>
                ))}
              </select>
              <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </div>
          </Field>

          <div style={{ height: 1, background: '#f1f5f1' }} />
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Stock Count</p>

          {/* Current stock (readonly) */}
          <Field label="Current Stock (System)">
            <div style={{ padding: '11px 14px', borderRadius: 10, background: '#f0fdf4', border: '1.5px solid #a7f3d0', fontSize: 14, fontWeight: 600, color: '#065f46' }}>
              {currentStockDisplay}
            </div>
          </Field>

          {/* Actual stock */}
          <Field label="Actual Stock (Physical Count) *" hint="Enter the real physical count you have right now">
            <input className="v2-input" type="number" placeholder="0" value={actualStock}
              onChange={e => setActualStock(e.target.value)} min="0" step="0.01" required autoFocus />
          </Field>

          {/* Difference preview */}
          {actualStock !== '' && !isNaN(parseFloat(actualStock)) && currentStockDisplay !== '—' && (
            <div style={{ padding: '12px 16px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <p style={{ margin: 0, fontSize: 13, color: '#1d4ed8', fontWeight: 500 }}>
                Difference: <strong>{(parseFloat(actualStock) - parseFloat(currentStockDisplay) || 0).toFixed(2)}</strong> units
                {parseFloat(actualStock) < parseFloat(currentStockDisplay) ? ' (stock reduced)' : ' (stock increased)'}
              </p>
            </div>
          )}

          <div style={{ height: 1, background: '#f1f5f1' }} />
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Stock Thresholds</p>

          <Field label="Low Stock Threshold" hint="Item appears in the reorder list when stock falls to or below this number">
            <input className="v2-input" type="number" placeholder="e.g. 10"
              value={stockLimit} onChange={e => setStockLimit(e.target.value)} min="0" step="0.01" />
          </Field>

          <Field label="Target Stock" hint="The stock level you aim to maintain — used to calculate how much to reorder">
            <input className="v2-input" type="number" placeholder="e.g. 50"
              value={targetStock} onChange={e => setTargetStock(e.target.value)} min="0" step="0.01" />
          </Field>

          <button type="submit" className="v2-btn-submit" disabled={saving} style={{ fontSize: 15 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
